"use client";

import { cn } from "@repo/ui";
import { Highlighter } from "@repo/ui/components/open-source/highlighter";
import { GradientText } from "@repo/ui/components/shadcn/gradient-text";
import { Logo } from "@saas/shared/components/Logo";
import { PrimaryCta } from "@repo/ui/components/design-system/cta/primary-cta";
import { SecondaryCta } from "@repo/ui/components/design-system/cta/secondary-cta";
import {
	GLASS_PANEL_SHELL_FRAME_CLASSNAME,
	GLASS_PANEL_SHELL_FRAME_OPAQUE_CLASSNAME,
	glassPanelSurfaceFillForTone,
} from "@shared/lib/glass-panel-surface";
import { SparklesIcon } from "lucide-react";
import { useTheme } from "next-themes";
import type { ReactNode } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
	BorderBeam,
	type BorderBeamProps,
} from "@repo/ui/components/design-system/border-beam";

export type GlassPanelTone = "light" | "dark" | "system";
export type GlassPanelSurfaceVariant = "translucent" | "opaque";
/** Accent chrome for headers / signoffs only — not the panel shell fill. */
export type GlassPanelAccentColor = "lime";

interface GlassPanelAccentTokens {
	underlineRgba: string;
	highlightRgba: string;
	headerGradientStops: {
		light: { fromColor: string; viaColor: string; toColor: string };
		dark: { fromColor: string; viaColor: string; toColor: string };
	};
	borderBeamClassName: string;
}

/** Fallback when computed style is missing (`rounded-3xl` ≈ 1.5rem @ 16px root). */
const GLASS_PANEL_FALLBACK_BORDER_RADIUS_PX = 24;
/** Matches `border-6` on the panel shell — only used until first layout measure. */
const GLASS_PANEL_SURFACE_BORDER_WIDTH_FALLBACK_PX = 6;

function parseBorderTopLeftRadiusPx(computed: string): number {
	const raw = computed.trim();
	if (!raw || raw === "0px") {
		return GLASS_PANEL_FALLBACK_BORDER_RADIUS_PX;
	}
	const pxMatch = /^([\d.]+)px$/.exec(raw);
	if (pxMatch) {
		const n = Number(pxMatch[1]);
		return Number.isFinite(n) && n > 0
			? n
			: GLASS_PANEL_FALLBACK_BORDER_RADIUS_PX;
	}
	const remMatch = /^([\d.]+)rem$/.exec(raw);
	if (remMatch) {
		const n = Number(remMatch[1]);
		if (Number.isFinite(n) && n > 0) {
			return n * 16;
		}
	}
	return GLASS_PANEL_FALLBACK_BORDER_RADIUS_PX;
}

function maxUniformBorderWidthPx(cs: CSSStyleDeclaration): number {
	const sides = [
		cs.borderTopWidth,
		cs.borderRightWidth,
		cs.borderBottomWidth,
		cs.borderLeftWidth,
	];
	let max = 0;
	for (const raw of sides) {
		const n = Number.parseFloat(raw);
		if (Number.isFinite(n) && n > max) {
			max = n;
		}
	}
	return max;
}

const GLASS_PANEL_ACCENT_TOKENS: Record<
	GlassPanelAccentColor,
	GlassPanelAccentTokens
> = {
	lime: {
		underlineRgba: "rgba(22,101,52,0.3)",
		highlightRgba: "rgba(163, 230, 53, 0.35)",
		headerGradientStops: {
			light: {
				fromColor: "#65a30d",
				viaColor: "#4d7c0f",
				toColor: "#3f6212",
			},
			dark: {
				fromColor: "#a3e635",
				viaColor: "#bef264",
				toColor: "#d9f99d",
			},
		},
		borderBeamClassName: "from-lime-800 via-lime-600 to-transparent",
	},
};

function glassPanelSurfaceClassName(params: {
	tone: GlassPanelTone;
	surfaceVariant: GlassPanelSurfaceVariant;
}): string {
	const shellFrame =
		params.surfaceVariant === "opaque"
			? GLASS_PANEL_SHELL_FRAME_OPAQUE_CLASSNAME
			: GLASS_PANEL_SHELL_FRAME_CLASSNAME;
	return cn(
		shellFrame,
		"px-6 py-8 text-center",
		glassPanelSurfaceFillForTone(params.tone, params.surfaceVariant),
	);
}

export interface GlassPanelProps {
	children: ReactNode;
	className?: string;
	/**
	 * Shell border/fill: `light` / `dark` force the matching tokens; **`system`** (default) follows
	 * `next-themes` so app dark mode gets the dark surface token.
	 */
	tone?: GlassPanelTone;
	/** `translucent` (default): frosted white / marketing surface. `opaque`: solid fills, no blur. */
	surfaceVariant?: GlassPanelSurfaceVariant;
	/** Keeps existing centering + `max-w-lg` behavior by default. */
	centered?: boolean;
	maxWidthClassName?: string;
	/** Optional animated border beam (rendered inside the same overflow-hidden shell). */
	borderBeam?: false | BorderBeamProps;
}

export function GlassPanel({
	children,
	className,
	tone = "system",
	surfaceVariant = "translucent",
	centered = true,
	maxWidthClassName = "max-w-lg",
	borderBeam = false,
}: GlassPanelProps) {
	const shellRef = useRef<HTMLDivElement>(null);
	const [innerCornerRadiusPx, setInnerCornerRadiusPx] = useState(() =>
		Math.max(
			0,
			GLASS_PANEL_FALLBACK_BORDER_RADIUS_PX -
				GLASS_PANEL_SURFACE_BORDER_WIDTH_FALLBACK_PX,
		),
	);

	const surface = cn(
		glassPanelSurfaceClassName({ tone, surfaceVariant }),
		centered && "mx-auto w-full",
		centered && maxWidthClassName,
		className,
		"rounded-3xl",
	);

	// Intentional deps: `surface` is not read in the closure but must re-sync when shell classes change
	useLayoutEffect(() => {
		if (!borderBeam) {
			return;
		}
		const el = shellRef.current;
		if (!el) {
			return;
		}
		const measure = () => {
			const cs = getComputedStyle(el);
			const outerPx = parseBorderTopLeftRadiusPx(cs.borderTopLeftRadius);
			const borderPx = maxUniformBorderWidthPx(cs);
			setInnerCornerRadiusPx(Math.max(0, outerPx - borderPx));
		};
		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => {
			ro.disconnect();
		};
	}, [borderBeam, surface]);

	const beamNode = borderBeam ? (
		<div
			className="pointer-events-none absolute inset-0 z-[5] overflow-hidden"
			style={{ borderRadius: `${innerCornerRadiusPx}px` }}
		>
			<BorderBeam
				{...borderBeam}
				pathCornerRadiusPx={
					borderBeam.pathCornerRadiusPx ?? innerCornerRadiusPx
				}
			/>
		</div>
	) : null;

	return (
		<div ref={shellRef} className={surface}>
			{children}
			{beamNode}
		</div>
	);
}

export type GlassPanelLogoVariant = "icon" | "full";

export function GlassPanelLogo({
	variant = "icon",
	className,
}: {
	variant?: GlassPanelLogoVariant;
	className?: string;
}) {
	return (
		<span
			className={cn("inline-flex cursor-default select-none", className)}
		>
			<Logo iconOnly={variant === "icon"} disableLink />
		</span>
	);
}

export function GlassPanelHeader({
	children,
	color = "lime",
}: {
	children: string;
	color?: GlassPanelAccentColor;
}) {
	const tokens = GLASS_PANEL_ACCENT_TOKENS[color];
	const { resolvedTheme } = useTheme();
	const [hasClientTheme, setHasClientTheme] = useState(false);
	useEffect(() => {
		setHasClientTheme(true);
	}, []);
	const stops =
		hasClientTheme && resolvedTheme === "dark"
			? tokens.headerGradientStops.dark
			: tokens.headerGradientStops.light;
	return (
		<Highlighter action="underline" color={tokens.underlineRgba} isView>
			<h1 className="inline font-black text-2xl uppercase tracking-wide">
				<GradientText
					text={children}
					className="inline"
					fromColor={stops.fromColor}
					viaColor={stops.viaColor}
					toColor={stops.toColor}
				/>
			</h1>
		</Highlighter>
	);
}

export function GlassPanelSubheader({ children }: { children: ReactNode }) {
	return (
		<h2 className="mt-6 font-extrabold text-lg text-slate-900 dark:text-neutral-100">
			<SparklesIcon className="relative -top-0.5 mr-2 inline size-4 animate-pulse text-amber-500" />
			{children}
			<SparklesIcon
				className="relative -top-0.5 ml-2 inline size-4 animate-pulse text-amber-500"
				style={{
					transform: "scaleX(-1)",
					animationDelay: "0.5s",
				}}
			/>
		</h2>
	);
}

export function GlassPanelSubtext({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) {
	return (
		<p
			className={cn(
				"mt-2 mx-auto max-w-sm text-sm text-slate-600 dark:text-neutral-400",
				className,
			)}
		>
			{children}
		</p>
	);
}

export function GlassPanelSectionTitle({ children }: { children: ReactNode }) {
	return (
		<h1 className="font-display text-2xl font-bold text-foreground">
			{children}
		</h1>
	);
}

export function GlassPanelSignoff({
	signoff,
	teamPrefix,
	teamSuffix,
	color = "lime",
}: {
	signoff: ReactNode;
	teamPrefix: ReactNode;
	teamSuffix: ReactNode;
	color?: GlassPanelAccentColor;
}) {
	const tokens = GLASS_PANEL_ACCENT_TOKENS[color];
	return (
		<>
			<p className="mt-6 text-lg text-slate-600 dark:text-neutral-400">
				{signoff}
			</p>
			<p className="mt-2 flex flex-wrap items-center justify-center gap-x-2 text-lg text-slate-700 dark:text-neutral-300">
				{teamPrefix}
				<Highlighter
					action="highlight"
					color={tokens.highlightRgba}
					isView
				>
					<span className="mx-2 inline-flex h-6 items-center align-middle [&_img]:h-6 [&_img]:w-auto">
						<Logo disableLink />
					</span>
				</Highlighter>
				{teamSuffix}
			</p>
		</>
	);
}

export function GlassPanelCtas({
	primary,
	secondary,
}: {
	primary?: ReactNode;
	secondary?: ReactNode;
}) {
	if (!primary && !secondary) {
		return null;
	}
	return (
		<div className="mt-8 flex w-full flex-col items-stretch gap-3 sm:flex-row sm:justify-center">
			{secondary ? (
				<SecondaryCta
					asChild
					ctaSize="medium"
					ctaWidth="full"
					className="w-full sm:min-w-[10rem]"
				>
					{secondary}
				</SecondaryCta>
			) : null}
			{primary ? (
				<PrimaryCta
					asChild
					ctaSize="medium"
					ctaWidth="full"
					className="w-full sm:min-w-[10rem]"
				>
					{primary}
				</PrimaryCta>
			) : null}
		</div>
	);
}
